import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import DropDownMultiple from "../../components/staff/DropDownMultiple";
import { getOfficesByFirmId } from "../../Services/Firm";
import { fetchPersonByFirm } from "../../Services/Person";
import type { FirmContactView } from "../../interfaces/models/IFirmContact";
import {
  contactsForFirmContactDropdown,
  contactsForOfficeContactDropdown,
  formatPersonsForContactDropdown,
  type ContactDropdownPerson,
} from "../../utils/firmContactUtils";
import { formatDataAdminPersonPickerName } from "../ManageFirm/Professionals/manageDataAdminOfficeRules";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import { toReadableLabel } from "../../utils/contactOptionLabel";
import {
  getAccountingFirmContact,
  officeDisplayName,
  type AccountingOfficeDetail,
} from "../../utils/accountingOfficeDetail";
import { buildAccountingFirmOfficeGroups } from "../../utils/accountingOfficeTableGrouping";
import { formatCalendarDateToString } from "../../utils/dates";
import {
  resolveProfessionalsCountError,
  isInvestmentBankingFirm,
} from "../../utils/accountingFeesCalculations";
import {
  type AccountingParametersForm,
  saveAccountingParameters,
} from "../../utils/saveAccountingParameters";
import {
  AccountingModalError,
  AccountingModalFooter,
  AccountingModalHeader,
  AccountingModalSectionTitle,
  isoToDisplay,
} from "./accountingModalUi";
import { SECTION_TITLE_CLASS } from "./accountingFirmPanels";

const FORM_GRID_CLASS = "grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2";

const ADDRESS_GRID_CLASS =
  "grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3";

type EditAccountingParametersModalProps = {
  office: AccountingOfficeDetail;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
};

const EMPTY_FORM: AccountingParametersForm = {
  legalName: "",
  accountingContactId: "",
  ccContactId: "",
  address1: "",
  city: "",
  state: "",
  country: "",
  dateJoinedDisplay: "",
  dateWithdrawnDisplay: "",
  billingStartDateDisplay: "",
  jurisdictionSize: "",
};

function dateFieldToDisplay(value: string | Date | undefined | null): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return isoToDisplay(value.trim().slice(0, 10));
  }
  return formatCalendarDateToString(value);
}

function resolveSingleContactValue(
  contactId: string,
  options: ContactDropdownPerson[],
  displayName?: string,
): ContactDropdownPerson | null {
  const id = String(contactId ?? "").trim();
  if (!id) {
    return null;
  }
  return (
    options.find((option) => String(option.id) === id) ?? {
      id,
      name: toReadableLabel(displayName) || "Selected professional",
    }
  );
}

export default function EditAccountingParametersModal({
  office,
  isOpen,
  onClose,
  onSaved,
}: EditAccountingParametersModalProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [firmContact, setFirmContact] = useState<FirmContactView | null>(null);
  const [contactOptions, setContactOptions] = useState<ContactDropdownPerson[]>(
    [],
  );
  const [ccContactOptions, setCcContactOptions] = useState<
    ContactDropdownPerson[]
  >([]);
  const [form, setForm] = useState<AccountingParametersForm>(EMPTY_FORM);
  const [readOnlyAccountingContactName, setReadOnlyAccountingContactName] =
    useState("");
  const [isSecondary, setIsSecondary] = useState(false);

  const isInvestmentBanking = isInvestmentBankingFirm({
    serviceType: office.serviceType,
  });

  const professionalsError = useMemo(() => {
    if (isInvestmentBanking) {
      return null;
    }
    return resolveProfessionalsCountError({
      serviceType: office.serviceType,
      firmProfessionalsInPrimary: form.jurisdictionSize,
      professionalsInPrimary: form.jurisdictionSize,
    });
  }, [form.jurisdictionSize, isInvestmentBanking, office.serviceType]);

  const accountingContactMissing =
    !isSecondary && !String(form.accountingContactId ?? "").trim();

  const submitDisabled =
    Boolean(professionalsError) || accountingContactMissing || loading;

  const patchForm = useCallback((patch: Partial<AccountingParametersForm>) => {
    setForm((current) => ({ ...current, ...patch }));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    setFirmContact(null);
    setContactOptions([]);
    setCcContactOptions([]);
    setForm(EMPTY_FORM);
    setReadOnlyAccountingContactName("");
    setIsSecondary(false);

    void (async () => {
      try {
        const firmId = String(office.firm ?? "").trim();
        if (!firmId) {
          throw new Error("Missing firm id");
        }

        const secondary =
          String(office.membershipType ?? "").toLowerCase() === "secondary";

        const [officeContact, persons, siblings] = await Promise.all([
          getAccountingFirmContact(office).catch(() => null),
          fetchPersonByFirm(firmId),
          getOfficesByFirmId(firmId),
        ]);

        if (cancelled) {
          return;
        }

        const firmOffices = siblings ?? [];
        const firmPersons = Array.isArray(persons) ? persons : [];
        const allContacts = formatPersonsForContactDropdown(firmPersons);
        const accountingContactId = officeContact?.accountingContact?.id ?? "";
        const ccContactId = officeContact?.ccContact?.id ?? "";

        const dropdownOptions = contactsForOfficeContactDropdown(
          allContacts,
          office.id,
          "accountingContact",
          accountingContactId,
        );
        const ccDropdownOptions = contactsForFirmContactDropdown(
          firmPersons.flatMap((person) => {
            const id = String(person?.id ?? "").trim();
            const name = formatDataAdminPersonPickerName(person, firmOffices);
            if (!id || !name) {
              return [];
            }
            return [
              {
                id,
                name,
                isAttorney: person?.isAttorney,
                mainOffice: person?.mainOffice,
                additionalOffices: person?.additionalOffices,
              },
            ];
          }),
          "ccContact",
          ccContactId,
        );

        let inheritedContactName = "";
        if (secondary) {
          const groups = buildAccountingFirmOfficeGroups(siblings);
          const group = groups.get(firmId);
          const primaryOfficeId = group?.parentOfficeId;
          if (primaryOfficeId && primaryOfficeId !== office.id) {
            const primaryContact = await getAccountingFirmContact({
              id: primaryOfficeId,
              firm: firmId,
              membershipType: "primary",
            }).catch(() => null);
            inheritedContactName =
              primaryContact?.accountingContact?.fullName?.trim() ||
              primaryContact?.accountingContact?.id ||
              "";
          } else if (officeContact?.accountingContact?.fullName) {
            inheritedContactName =
              officeContact.accountingContact.fullName.trim();
          }
        }

        const jurisdictionSize =
          office.firmProfessionalsInPrimary ??
          (office.professionalsInPrimary != null
            ? String(office.professionalsInPrimary)
            : "");

        setFirmContact(officeContact ?? null);
        setContactOptions(dropdownOptions);
        setCcContactOptions(ccDropdownOptions);
        setIsSecondary(secondary);
        setReadOnlyAccountingContactName(inheritedContactName);
        setForm({
          legalName: String(office.firmLegalName ?? office.firmName ?? "").trim(),
          accountingContactId: String(accountingContactId),
          ccContactId: String(ccContactId),
          address1: String(office.address ?? ""),
          city: String(office.cityName ?? ""),
          state: String(office.stateName ?? ""),
          country: String(office.countryName ?? ""),
          dateJoinedDisplay: dateFieldToDisplay(office.dateJoined),
          dateWithdrawnDisplay: dateFieldToDisplay(office.dateWithdrawn),
          billingStartDateDisplay: dateFieldToDisplay(office.billingStartDate),
          jurisdictionSize: String(jurisdictionSize ?? ""),
        });
      } catch {
        if (!cancelled) {
          setError("Unable to load accounting parameters.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, office]);

  const handleSave = async () => {
    setError("");
    if (professionalsError) {
      setError(professionalsError);
      return;
    }
    if (accountingContactMissing) {
      setError("Accounting Contact is required.");
      return;
    }

    setSaving(true);
    try {
      await saveAccountingParameters({
        office,
        firmContact,
        form,
        isSecondary,
      });
      onSaved();
      onClose();
    } catch {
      setError("Unable to save accounting parameters. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedAccountingContact = resolveSingleContactValue(
    form.accountingContactId,
    contactOptions,
    firmContact?.accountingContact?.fullName,
  );

  const selectedCcContact = resolveSingleContactValue(
    form.ccContactId,
    ccContactOptions,
    firmContact?.ccContact?.fullName,
  );

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={onClose}
      title="Edit Parameters"
      titleColor="text-primary font-decimal text-2xl font-medium leading-8"
      compact
      compactBodyScrolls
      className="max-w-3xl"
    >
      <div className="flex flex-col gap-5">
        <AccountingModalHeader
          firmName={officeDisplayName(office)}
          jurisdiction={getJurisdictionLabel(office)}
        />

        {loading ? (
          <p className="py-8 text-center text-gray-font">
            Loading parameters...
          </p>
        ) : (
          <>
            <AccountingModalSectionTitle>Overview</AccountingModalSectionTitle>

            <div className="flex flex-col gap-6">
              <section className="flex flex-col gap-4">
                <h4 className={SECTION_TITLE_CLASS}>Firm &amp; contact</h4>
                <div className={FORM_GRID_CLASS}>
                  {isSecondary ? (
                    <div className="flex flex-col gap-2 sm:col-span-2">
                      <span className="text-sm font-medium text-gray-font">
                        Accounting Contact
                      </span>
                      <p className="text-base text-gray-font">
                        {readOnlyAccountingContactName || "—"}
                      </p>
                      <p className="text-xs text-gray-font">
                        Inherited from the firm&apos;s primary office.
                      </p>
                    </div>
                  ) : (
                    <div className="min-w-0 sm:col-span-2">
                      <DropDownMultiple<ContactDropdownPerson>
                        title="Accounting Contact"
                        required
                        options={contactOptions}
                        value={selectedAccountingContact}
                        placeholder="Select a professional"
                        renderInPortal
                        onChange={(selected) => {
                          patchForm({
                            accountingContactId: selected
                              ? String(selected.id)
                              : "",
                          });
                        }}
                      />
                    </div>
                  )}

                  <div className="min-w-0 sm:col-span-2">
                    <DropDownMultiple<ContactDropdownPerson>
                      title="CC - Contact"
                      options={ccContactOptions}
                      value={selectedCcContact}
                      placeholder="Select a professional"
                      renderInPortal
                      onChange={(selected) => {
                        patchForm({
                          ccContactId: selected ? String(selected.id) : "",
                        });
                      }}
                    />
                  </div>

                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <Input
                      title="Company Legal Name"
                      value={form.legalName}
                      onChange={(e) => patchForm({ legalName: e.target.value })}
                      error={false}
                    />
                    <p className="text-xs text-gray-font">
                      (Used for accounting invoices)
                    </p>
                  </div>
                </div>
              </section>

              <section className="flex flex-col gap-4">
                <h4 className={SECTION_TITLE_CLASS}>
                  Accounting mailing address
                </h4>
                <div className={ADDRESS_GRID_CLASS}>
                  <div className="min-w-0 sm:col-span-2 lg:col-span-3">
                    <Input
                      title="Address"
                      value={form.address1}
                      onChange={(e) => patchForm({ address1: e.target.value })}
                      error={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <Input
                      title="City"
                      value={form.city}
                      onChange={(e) => patchForm({ city: e.target.value })}
                      error={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <Input
                      title="State/Province/Territory"
                      value={form.state}
                      onChange={(e) => patchForm({ state: e.target.value })}
                      error={false}
                    />
                  </div>
                  <div className="min-w-0">
                    <Input
                      title="Country"
                      value={form.country}
                      onChange={(e) => patchForm({ country: e.target.value })}
                      error={false}
                    />
                  </div>
                </div>
              </section>

              <section className="flex flex-col gap-4">
                <h4 className={SECTION_TITLE_CLASS}>
                  Membership &amp; billing
                </h4>
                <div className={FORM_GRID_CLASS}>
                  <div className="min-w-0">
                    <CalendarInput
                      title="Joined Date"
                      value={form.dateJoinedDisplay}
                      defaultValue={form.dateJoinedDisplay}
                      onChange={(date: string) =>
                        patchForm({ dateJoinedDisplay: date })
                      }
                      type="single"
                    />
                  </div>
                  <div className="min-w-0">
                    <CalendarInput
                      title="Withdrawn Date"
                      value={form.dateWithdrawnDisplay}
                      defaultValue={form.dateWithdrawnDisplay}
                      onChange={(date: string) =>
                        patchForm({ dateWithdrawnDisplay: date })
                      }
                      type="single"
                    />
                  </div>
                  <div className="min-w-0">
                    <CalendarInput
                      title="First Dues Date"
                      value={form.billingStartDateDisplay}
                      defaultValue={form.billingStartDateDisplay}
                      onChange={(date: string) =>
                        patchForm({ billingStartDateDisplay: date })
                      }
                      type="single"
                    />
                  </div>
                  <div className="min-w-0">
                    <Input
                      title="Jurisdiction Size"
                      type="number"
                      value={form.jurisdictionSize}
                      onChange={(e) =>
                        patchForm({ jurisdictionSize: e.target.value })
                      }
                      error={Boolean(professionalsError)}
                      errorMsj={professionalsError ?? ""}
                      required={!isInvestmentBanking}
                    />
                  </div>
                </div>
              </section>
            </div>

            <AccountingModalError message={error} />
          </>
        )}

        <AccountingModalFooter
          onCancel={onClose}
          onSubmit={() => void handleSave()}
          submitText="Save"
          saving={saving}
          submitDisabled={submitDisabled || loading}
        />
      </div>
    </Modal>
  );
}
